import {
  call,
  cancelled,
  put,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { DOCUMENT } from "./actionTypes";
import {
  duplicateDocument,
  getListaDocument,
  getDocument,
  getDocumentHistory,
  getDocumentHistoryList,
  saveDocument,
  getDocumentProducts,
  getDocumentSections,
  getDocumentSnippets,
  getDocumentPreview,
  getDownloadDocument,
  postDocumentDownloadList,
  getDocumentDownloadHistory,
  putDocumentBulkStatusUpdate,
} from "./actions";
import { emptyStringToNull } from "../../helpers/null_empty_string_helper";
import { DOCUMENT_URL } from "../../helpers/url_api_helper";
import moment from "moment";
import { normalizeApiError } from "../../helpers/utils/normalizeApiError";

function* listarDocuments({ payload }) {
  const controller = new AbortController();
  try {
    emptyStringToNull(payload);
    const res = yield call(DOCUMENT_URL.getDocumentLista, payload, {
      signal: controller.signal,
    });
    yield put(getListaDocument.success(res));
  } catch (error) {
      yield put(
        getListaDocument.fail(
          normalizeApiError(
            error,
            "Something went wrong retrieving the system's documents, please try again later",
          ),
        ),
      );
  } finally {
    if(yield cancelled()) {
      controller.abort();
    }
  }
}

function* obterDocument({ payload }) {
  const { documentId } = payload;

  try {
    const document = yield call(DOCUMENT_URL.getDocument, documentId);

    const res = {
      ...document,
      createDate: moment(document.createDate).format("DD MMM/YY hh:mm A"),
      updateDate: document.updateDate
        ? moment(document.updateDate).format("DD MMM/YY hh:mm A")
        : "",
      products: String(document.products[0]),
    };

    
    yield put(getDocument.success(res));
  } catch (error) {
    yield put(
      getDocument.fail(
        normalizeApiError(error, "Something went wrong obtaining the document"),
      ),
    );
  }
}

function* salvarDocument({ payload }) {
  const { body, method } = payload;
  body.products = [body.products];
  
  try {
    if (method === "POST") {
      yield call(DOCUMENT_URL.postDocument, body);
    } else if (method === "PUT") {
      yield call(DOCUMENT_URL.putDocument, body);
    } else if (method === "PUT-REVIEW") {
      yield call(DOCUMENT_URL.putDocumentSaveAndSendToReview, body);
    }

    yield put(saveDocument.success(true));
  } catch (error) {
    yield put(
      saveDocument.fail(
        normalizeApiError(
          error,
          "Something went wrong saving the document, please try again later",
        ),
      ),
    );
  }
}

function* duplicarDocument({ payload }) {
  const { documentId } = payload;

  try {
    const res = yield call(DOCUMENT_URL.postDocumentDuplicate, documentId);
    yield put(duplicateDocument.success(res));
  } catch (error) {
    yield put(
      duplicateDocument.fail(
        normalizeApiError(
          error,
          "Something went wrong retrieving the system's document, please try again later",
        ),
      ),
    );
  }
}

function* listarDocumentsHistory({ payload }) {
  try {
    const { documentId, page } = payload;
    const res = yield call(DOCUMENT_URL.getDocumentHistoryList, documentId, page);
    yield put(getDocumentHistoryList.success(res));
  } catch (error) {
    yield put(
      getDocumentHistoryList.fail(
        normalizeApiError(
          error,
          "Something went wrong retrieving the system's document history",
        ),
      ),
    );
  }
}

function* obterDocumentHistory({ payload }) {
  const { idHistory } = payload;

  try {
    const document = yield call(DOCUMENT_URL.getDocumentHistory, idHistory);

    const res = {
      ...document,
      createDate: moment(document.createDate).format("DD MMM/YY hh:mm A"),
      updateDate: document.updateDate
        ? moment(document.updateDate).format("DD MMM/YY hh:mm A")
        : "",
    };

    yield put(getDocumentHistory.success(res));
  } catch (error) {
    yield put(
      getDocumentHistory.fail(
        normalizeApiError(
          error,
          "Something went wrong retrieving the system's document history",
        ),
      ),
    );
  }
}

function* obterDocumentProducts({ payload }) {
  const { documentId } = payload;
  try {
    const res = yield call(DOCUMENT_URL.getDocumentProducts, documentId);
    yield put(getDocumentProducts.success(res));
  } catch (error) {
    yield put(
      getDocumentProducts.fail(
        normalizeApiError(
          error,
          "Something went wrong retrieving the system's snippet products, please try again later",
        ),
      ),
    );
  }
}

function* obterDocumentSections({ payload }) {
  const { documentId } = payload;
  try {
    const res = yield call(DOCUMENT_URL.getDocumentSections, documentId);
    yield put(getDocumentSections.success(res));
  } catch (error) {
    yield put(
      getDocumentSections.fail(
        normalizeApiError(
          error,
          "Something went wrong retrieving the system's snippet products, please try again later",
        ),
      ),
    );
  }
}

function* obterDocumentSnippets({ payload }) {
  const { documentId } = payload;
  try {
    const res = yield call(DOCUMENT_URL.getDocumentSnippets, documentId);
    yield put(getDocumentSnippets.success(res));
  } catch (error) {
    yield put(
      getDocumentSnippets.fail(
        normalizeApiError(
          error,
          "Something went wrong retrieving the system's snippet products, please try again later",
        ),
      ),
    );
  }
}

function* obterDocumentPreview({ payload }) {
  const { documentId } = payload;
  try {
    const res = yield call(DOCUMENT_URL.getDocumentPreview, documentId);
    yield put(getDocumentPreview.success(res));
  } catch (error) {
    yield put(
      getDocumentPreview.fail(
        normalizeApiError(
          error,
          "Something went wrong retrieving the system's document preview, please try again later",
        ),
      ),
    );
  }
}

function* obterDownloadDocument({ payload }) {
  const { documentId, type } = payload;

  try {
    const res = yield call(DOCUMENT_URL.getDocumentDownload, documentId, type);
    res.extension = type;
    yield put(getDownloadDocument.success(res));
  } catch (error) {
    yield put(
      getDownloadDocument.fail(
        normalizeApiError(
          error,
          "Something went wrong retrieving the system's document download, please try again later",
        ),
      ),
    );
  }
}

function* obterDownloadDocumentList({ payload }) {
  const { objFilter } = payload;
  try {
    const res = yield call(DOCUMENT_URL.postDocumentDownloadList, objFilter);
    yield put(postDocumentDownloadList.success(res));
  } catch (error) {
    yield put(
      postDocumentDownloadList.fail(
        normalizeApiError(
          error,
          "Something went wrong retrieving the system's document download list, please try again later",
        ),
      ),
    );
  }
}

function* obterDownloadDocumentHistory({ payload }) {
  const { documentId, type } = payload;

  try {
    const res = yield call(DOCUMENT_URL.getDocumentDownloadHistory, documentId, type);
    res.extension = type;
    yield put(getDocumentDownloadHistory.success(res));
  } catch (error) {
    yield put(
      getDocumentDownloadHistory.fail(
        normalizeApiError(
          error,
          "Something went wrong retrieving the system's document download, please try again later",
        ),
      ),
    );
  }
}


function* salvarDocumentBulkStatusUpdate({ payload }) {
  const { body, type } = payload;

  const statusMap = {
    Draft: DOCUMENT_URL.putDocumentBulkReview,
    Approved: DOCUMENT_URL.putDocumentBulkApproved,
    Rejected: DOCUMENT_URL.putDocumentBulkReject,
    Publish: DOCUMENT_URL.putDocumentBulkPublish,
    Obsolete: DOCUMENT_URL.putDocumentBulkObsolete,
  };

  try {
    const requestFunction = statusMap[type];

    if (!requestFunction) {
      throw new Error(`Invalid bulk status type: ${type}`);
    }

    const res = yield call(requestFunction, body);

    yield put(putDocumentBulkStatusUpdate.success(res));
  } catch (error) {
    yield put(
      putDocumentBulkStatusUpdate.fail(
        normalizeApiError(
          error,
          "Something went wrong updating the documents status, please try again later",
        ),
      ),
    );
  }
}

function* DocumentSaga() {
  yield takeLatest(DOCUMENT.GET_LIST.REQUEST, listarDocuments);
  yield takeEvery(DOCUMENT.GET_DOCUMENT.REQUEST, obterDocument);
  yield takeEvery(DOCUMENT.SAVE_DOCUMENT.REQUEST, salvarDocument);
  yield takeEvery(DOCUMENT.DUPLICATE_DOCUMENT.REQUEST, duplicarDocument);

  yield takeEvery(
    DOCUMENT.GET_DOCUMENT_HISTORY_LIST.REQUEST,
    listarDocumentsHistory,
  );
  yield takeEvery(DOCUMENT.GET_DOCUMENT_HISTORY.REQUEST, obterDocumentHistory);
  yield takeEvery(
    DOCUMENT.GET_DOCUMENT_PRODUCTS.REQUEST,
    obterDocumentProducts,
  );
  yield takeEvery(
    DOCUMENT.GET_DOCUMENT_SECTIONS.REQUEST,
    obterDocumentSections,
  );
  yield takeEvery(
    DOCUMENT.GET_DOCUMENT_SNIPPETS.REQUEST,
    obterDocumentSnippets,
  );
  yield takeEvery(DOCUMENT.GET_DOCUMENT_PREVIEW.REQUEST, obterDocumentPreview);
  yield takeEvery(DOCUMENT.GET_DOWNLOAD_DOCUMENT.REQUEST, obterDownloadDocument);

  yield takeEvery(DOCUMENT.POST_DOCUMENT_DOWNLOAD_LIST.REQUEST, obterDownloadDocumentList);

  yield takeEvery(DOCUMENT.GET_DOCUMENT_DOWNLOAD_HISTORY.REQUEST, obterDownloadDocumentHistory);

  yield takeEvery(DOCUMENT.PUT_DOCUMENT_BULK_STATUS_UPDATE.REQUEST, salvarDocumentBulkStatusUpdate);
}

export default DocumentSaga;
